import { formatPaise } from '@tkm/money'
import type { ScanBarState } from '../shell/scanFocus'
import { StationShell } from '../ui/StationShell'
import type { CameraState } from '../ui/ScanBar'

/**
 * Mary's station. 56px targets, one level of menu, words plus icon on every
 * instruction, and no slot anywhere for a customer - the server enforces that
 * (SEC), and the design does not even leave a place to put one.
 *
 * The choices come in as props from the cached bootstrap snapshot, so a cold
 * station with no network still renders a usable form (SYN-09).
 */
export interface CatalogueUnit {
  id: string
  title: string
  format: string
  ageGroup: string
  grade: 'A' | 'B' | 'C'
  priceInPaise: number
  defects: string[]
}

export interface IntakeCatalogueProps {
  scan: ScanBarState
  camera?: CameraState
  /** From useCamera - one stream decodes the labels and takes the catalogue photo. */
  videoRef?: React.Ref<HTMLVideoElement>
  onCapturePhoto?: () => void
  tub: { code: string; label: string; units: number } | null
  unit: CatalogueUnit | null
  grades: string[]
  defectTypes: string[]
  todayCount: number
  pendingDrafts?: number
  online?: boolean
}

/** The house caption format, previewed live as fields change. */
export function captionFor(unit: CatalogueUnit): string[] {
  return [
    unit.title.toUpperCase(),
    unit.format,
    `Age ${unit.ageGroup}`,
    `Condition ${unit.grade}`,
    formatPaise(unit.priceInPaise),
    ...(unit.defects.length ? [unit.defects.join(', ')] : []),
  ]
}

export function IntakeCatalogue({
  scan,
  camera,
  videoRef,
  onCapturePhoto,
  tub,
  unit,
  grades,
  defectTypes,
  todayCount,
  pendingDrafts = 0,
  online = true,
}: IntakeCatalogueProps) {
  return (
    <StationShell
      title="Catalogue"
      progress={`${todayCount} this session`}
      queue={tub?.units}
      scan={scan}
      camera={camera}
      videoRef={videoRef}
      onCapturePhoto={onCapturePhoto}
      pendingDrafts={pendingDrafts}
      online={online}
    >
      {tub ? (
        <p className="t-label muted">
          {tub.label} - {tub.code}
        </p>
      ) : (
        <p className="t-label muted">No tub open</p>
      )}

      {unit ? (
        <>
          <section className="card caption-preview">
            {captionFor(unit).map((line) => (
              <p key={line} className="caption-line">
                {line}
              </p>
            ))}
          </section>

          <section className="card">
            <p className="t-label">Condition</p>
            <div className="chip-row">
              {grades.map((g) => (
                <span key={g} className={`chip chip-grade${g === unit.grade ? ' chip-on' : ''}`}>
                  {g}
                </span>
              ))}
            </div>
            <p className="t-label">Defects</p>
            <div className="chip-row">
              {defectTypes.map((d) => (
                <span key={d} className={`chip chip-defect${unit.defects.includes(d) ? ' chip-on' : ''}`}>
                  {d}
                </span>
              ))}
            </div>
          </section>
        </>
      ) : (
        <p className="empty">Scan a label to start a book.</p>
      )}
    </StationShell>
  )
}
